/* Cookman EU - Wholesale Quick Order */
(() => {
  if (window.cookmanQuickOrderLoaded) return;
  window.cookmanQuickOrderLoaded = true;

  const root = window.Shopify?.routes?.root || '/';
  const getRows = (box) => [...box.querySelectorAll('.cqo__row')];

  function showMessage(box, text) {
    const message = box.querySelector('.cqo__message');
    if (message) message.textContent = text;
  }

  function availableToAdd(row) {
    if (row.dataset.tracked !== 'true') return Infinity;
    const stock = Number(row.dataset.stock || 0);
    const alreadyInCart = Number(row.dataset.inCart || 0);
    return Math.max(0, stock - alreadyInCart);
  }

  function correctQuantity(row, notify = false) {
    const input = row.querySelector('.cqo__quantity');
    if (!input) return 0;

    const parsed = Number(input.value);
    const requested = Number.isFinite(parsed)
      ? Math.max(0, Math.floor(parsed))
      : 0;
    const maximum = availableToAdd(row);
    const corrected = Math.min(requested, maximum);
    input.value = String(corrected);

    if (notify && corrected < requested) {
      showMessage(
        row.closest('.cqo'),
        `Quantity adjusted to available stock: ${corrected}.`
      );
    }
    return corrected;
  }

  function updateForm(box) {
    const locked = box.dataset.busy === 'true' || box.dataset.ready !== 'true';
    let total = 0;

    getRows(box).forEach((row) => {
      const input = row.querySelector('.cqo__quantity');
      if (!input) return; // Sold-out variants have no quantity input.

      const max = availableToAdd(row);
      const qty = correctQuantity(row);
      total += qty;

      input.disabled = locked || max === 0;
      if (Number.isFinite(max)) input.max = String(max);
      else input.removeAttribute('max');

      row.querySelector('.cqo__minus').disabled = locked || qty === 0;
      row.querySelector('.cqo__plus').disabled = locked || qty >= max;
    });

    const addButton = box.querySelector('.cqo__add');
    addButton.disabled = locked || total === 0;
    addButton.textContent = box.dataset.busy === 'true'
      ? 'PLEASE WAIT...'
      : `ADD TO CART (${total})`;
  }

  async function readCart() {
    const response = await fetch(root + 'cart.js', {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json' }
    });
    if (!response.ok) throw new Error('Unable to retrieve your cart. Please try again.');
    return response.json();
  }

  async function refreshCartQuantities(box) {
    const cart = await readCart();
    const counts = new Map();

    cart.items.forEach((item) => {
      const id = String(item.variant_id);
      counts.set(id, (counts.get(id) || 0) + item.quantity);
    });

    getRows(box).forEach((row) => {
      row.dataset.inCart = String(counts.get(row.dataset.variantId) || 0);
    });
    updateForm(box);
    return cart;
  }

  function updateCartCount(cart) {
    const container = document.getElementById('cart-icon-bubble');
    if (!container) return;

    let bubble = container.querySelector('.cart-count-bubble');
    if (!bubble && cart.item_count > 0) {
      bubble = document.createElement('div');
      bubble.className = 'cart-count-bubble';
      const count = document.createElement('span');
      count.setAttribute('aria-hidden', 'true');
      bubble.append(count);
      container.append(bubble);
    }

    if (!bubble) return;
    if (cart.item_count === 0) {
      bubble.remove();
      return;
    }

    const count = bubble.querySelector('[aria-hidden="true"]');
    if (count) count.textContent = String(cart.item_count);
  }

  document.addEventListener('click', async (event) => {
    const toggle = event.target.closest('.cqo__toggle');
    if (toggle) {
      const box = toggle.closest('.cqo');
      const panel = box.querySelector('.cqo__panel');
      panel.hidden = !panel.hidden;
      toggle.setAttribute('aria-expanded', String(!panel.hidden));
      if (panel.hidden) return;

      box.dataset.ready = 'false';
      box.dataset.busy = 'true';
      showMessage(box, 'Checking availability...');
      updateForm(box);

      try {
        await refreshCartQuantities(box);
        box.dataset.ready = 'true';
        showMessage(box, '');
      } catch (error) {
        showMessage(box, error.message || 'Unable to check availability. Please try again.');
      } finally {
        box.dataset.busy = 'false';
        updateForm(box);
      }
      return;
    }

    const stepButton = event.target.closest('.cqo__minus, .cqo__plus');
    if (stepButton) {
      const row = stepButton.closest('.cqo__row');
      const box = row.closest('.cqo');
      if (box.dataset.ready !== 'true' || box.dataset.busy === 'true') return;

      const input = row.querySelector('.cqo__quantity');
      input.value = String(
        Number(input.value || 0) + (stepButton.matches('.cqo__plus') ? 1 : -1)
      );
      correctQuantity(row, true);
      updateForm(box);
      return;
    }

    const addButton = event.target.closest('.cqo__add');
    if (!addButton) return;

    const box = addButton.closest('.cqo');
    if (box.dataset.ready !== 'true' || box.dataset.busy === 'true') return;

    box.dataset.busy = 'true';
    showMessage(box, 'Checking cart quantities...');
    updateForm(box);

    try {
      await refreshCartQuantities(box);

      const items = getRows(box)
        .map((row) => ({
          id: Number(row.dataset.variantId),
          quantity: correctQuantity(row)
        }))
        .filter((item) => item.quantity > 0);

      if (items.length === 0) {
        showMessage(box, 'No available quantity to add.');
        return;
      }

      showMessage(box, 'Adding to cart...');
      const response = await fetch(root + 'cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({ items })
      });

      if (!response.ok) {
        throw new Error('Unable to add items to cart. Please check available stock and try again.');
      }

      const total = items.reduce((sum, item) => sum + item.quantity, 0);
      getRows(box).forEach((row) => {
        const input = row.querySelector('.cqo__quantity');
        if (input) input.value = '0';
      });

      showMessage(box, `${total} item${total === 1 ? '' : 's'} added to cart.`);

      try {
        const cart = await refreshCartQuantities(box);
        updateCartCount(cart);
      } catch (_) {
        showMessage(box, `${total} item${total === 1 ? '' : 's'} added. Refresh the cart to see the updated total.`);
      }
    } catch (error) {
      showMessage(box, error.message || 'Unable to add items to cart. Please try again.');
      try {
        await refreshCartQuantities(box);
      } catch (_) {
        box.dataset.ready = 'false';
      }
    } finally {
      box.dataset.busy = 'false';
      updateForm(box);
    }
  });

  document.addEventListener('input', (event) => {
    if (!event.target.matches('.cqo__quantity')) return;
    const row = event.target.closest('.cqo__row');
    const box = row.closest('.cqo');
    if (box.dataset.ready !== 'true' || box.dataset.busy === 'true') return;
    correctQuantity(row, true);
    updateForm(box);
  });
})();

